/**
 * Event times in the venue's time zone.
 *
 * The API stores instants in UTC and gives every league, tournament, drop-in
 * and match the IANA time zone of its venue (`timeZone` / `timezone`). A time
 * is shown at the venue's local time, so a 7 PM game in Toronto reads 7 PM
 * for everyone. A viewer in another zone also sees the venue's zone and their
 * own time: "7:00 PM Eastern Time · 4:30 AM your time".
 *
 * Forms send the wall time the host typed ("2026-12-15T19:00", no offset)
 * with the device's zone as a fallback; the server reads it in the venue's
 * zone (backend/src/utils/eventTime.js).
 *
 * Calendar dates (season dates, tournament days) are not instants: use
 * utils/dateOnly for those.
 */

/** The viewer's IANA zone ("America/Toronto"), or undefined when unknown. */
export const viewerTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
};

const validZone = (timeZone) => {
  if (!timeZone || typeof timeZone !== "string") return undefined;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return timeZone;
  } catch {
    return undefined;
  }
};

/** An event's zone from an API object (camel or snake case), or undefined. */
export const eventTimeZone = (item) => validZone(item?.timeZone) || validZone(item?.timezone);

const toDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Does the viewer see times in another zone than the event's? */
const isOtherZone = (timeZone, now = new Date()) => {
  const viewer = viewerTimeZone();
  if (!timeZone || !viewer || timeZone === viewer) return false;
  // Same offset right now (e.g. America/Toronto vs America/New_York): no label needed
  const offset = (zone) => new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "longOffset" })
    .formatToParts(now).find((p) => p.type === "timeZoneName")?.value;
  return offset(timeZone) !== offset(viewer);
};

/** Intl formatting that falls back to a shorter zone name, then none. */
const formatWithZoneName = (date, locale, options, timeZoneName) => {
  for (const name of [timeZoneName, "short", undefined]) {
    try {
      return new Intl.DateTimeFormat(locale, { ...options, ...(name ? { timeZoneName: name } : {}) }).format(date);
    } catch {
      // older engines lack "longGeneric"
    }
  }
  return date.toLocaleString(locale);
};

/**
 * Format an instant at the event's venue. `options` are Intl options (date
 * and/or time parts). With no zone it falls back to the viewer's local time.
 * The venue's zone ("Eastern Time") is added when the viewer is somewhere else,
 * unless `options` sets timeZoneName itself.
 */
export const formatEventTime = (value, timeZone, options = {}, locale = "en-US") => {
  const date = toDate(value);
  if (!date) return "";
  const zone = validZone(timeZone);
  // A caller that sets timeZoneName (even to undefined, for no label) decides it
  const showZone = zone && !options.timeStyle && !("timeZoneName" in options) && options.hour && isOtherZone(zone, date);
  return formatWithZoneName(date, locale, { ...options, ...(zone ? { timeZone: zone } : {}) }, showZone ? "longGeneric" : undefined);
};

/**
 * The same instant on the viewer's clock, for a viewer in another zone than
 * the venue: "4:30 AM your time", with the day when it differs from the
 * venue's ("Wed, Dec 16, 4:30 AM your time"). "" when the viewer is in the
 * venue's zone or either zone is unknown.
 */
export const formatViewerTime = (value, timeZone, locale = "en-US") => {
  const date = toDate(value);
  const zone = validZone(timeZone);
  const viewer = validZone(viewerTimeZone());
  if (!date || !zone || !viewer || !isOtherZone(zone, date)) return "";
  const sameDay = eventDayKey(date, zone) === eventDayKey(date, viewer);
  const options = sameDay
    ? { hour: "numeric", minute: "2-digit" }
    : { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };
  return `${new Intl.DateTimeFormat(locale, { ...options, timeZone: viewer }).format(date)} your time`;
};

/**
 * The venue's zone name at that instant ("Eastern Time"), for a viewer in
 * another zone; "" otherwise.
 */
export const eventZoneName = (value, timeZone, locale = "en-US") => {
  const date = toDate(value);
  const zone = validZone(timeZone);
  if (!date || !zone || !isOtherZone(zone, date)) return "";
  const text = formatWithZoneName(date, locale, { timeZone, hour: "numeric" }, "longGeneric");
  const hour = new Intl.DateTimeFormat(locale, { timeZone, hour: "numeric" }).format(date);
  return text.replace(hour, "").trim();
};

/** Venue time, then the viewer's own time when they are elsewhere. */
const withViewerTime = (text, value, timeZone) => {
  const mine = text && formatViewerTime(value, timeZone);
  return mine ? `${text} · ${mine}` : text;
};

/** "Tue, Dec 15, 7:00 PM" at the venue (plus the viewer's time when elsewhere). */
export const formatEventDateTime = (value, timeZone) => withViewerTime(
  formatEventTime(value, timeZone, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }),
  value, timeZone,
);

/** "7:00 PM" at the venue (plus the viewer's time when elsewhere). */
export const formatEventClock = (value, timeZone) => withViewerTime(
  formatEventTime(value, timeZone, { hour: "numeric", minute: "2-digit" }),
  value, timeZone,
);

/** Any formatEventTime call, plus the viewer's time when elsewhere. */
export const formatEventTimeForViewer = (value, timeZone, options = {}) => {
  const text = formatEventTime(value, timeZone, options);
  // A calendar day alone gets no clock time
  return options.hour || options.timeStyle ? withViewerTime(text, value, timeZone) : text;
};

/** The event's calendar day at the venue, as YYYY-MM-DD. */
export const eventDayKey = (value, timeZone) => {
  const date = toDate(value);
  if (!date) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: validZone(timeZone) || viewerTimeZone(), year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
};

/** The wall-clock fields of `date` in `timeZone`. */
const zonedParts = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone, hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(date);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value);
  return [get("year"), get("month"), get("day"), get("hour"), get("minute"), get("second")];
};

/**
 * A wall time at the venue (a league game's "2026-12-15" + "18:45") as a
 * Date, or null when invalid. Without a zone it is the viewer's local time.
 */
export const wallTimeToDate = (date, time = "00:00", timeZone) => {
  const d = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(date || ""));
  const t = /^(\d{1,2}):(\d{2})/.exec(String(time || "00:00"));
  if (!d || !t) return null;
  const [y, mo, da, h, mi] = [d[1], d[2], d[3], t[1], t[2]].map(Number);
  const zone = validZone(timeZone);
  if (!zone) {
    const local = new Date(y, mo - 1, da, h, mi);
    return Number.isNaN(local.getTime()) ? null : local;
  }
  const wall = Date.UTC(y, mo - 1, da, h, mi);
  const offsetAt = (ms) => {
    const [py, pmo, pd, ph, pmi, ps] = zonedParts(new Date(ms), zone);
    return Date.UTC(py, pmo - 1, pd, ph, pmi, ps) - Math.floor(ms / 1000) * 1000;
  };
  const first = wall - offsetAt(wall);
  // Re-check with the offset at the result, in case a DST change lies between
  const result = new Date(wall - offsetAt(first));
  return Number.isNaN(result.getTime()) ? null : result;
};

/**
 * A datetime-local input value ("2026-12-15T19:00") for an instant at the
 * venue, so editing shows the time the host set, wherever they are now.
 */
export const toEventInputValue = (value, timeZone) => {
  const date = toDate(value);
  if (!date) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: validZone(timeZone) || viewerTimeZone(), hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  }).formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
};
